/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Modal } from '@scuf/common';
import React, { useContext, useEffect, useState } from 'react';
import './DataUploadCommon.scss';
import ChooseDataTypeTab from './ChooseDataTypeTab/ChooseDataTypeTab';
import UploadTab, {
  maxSizeCheck,
  minSizeCheck,
  uploadTypeCheck
} from './UploadTab/UploadTab';
import { useFileUploadContext } from '../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../context/FileUploadContext/FileUploadActions';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import { ApiContext } from '../../../context/ApiContext';
import { isUploadDisabled } from '../../shared/Utils/Utils';
import {
  ManualFileUploadTypeList,
  POLLING_MILLI_SECONDS,
  UPLOAD_MODAL_REDIRECT_TIME
} from '../../shared/Utils/Constants';
import {
  getActivetabClassName,
  handleManualUploadClick
} from '../../shared/Utils/ManualUpload/ManualUploadUtils';
import ChooseServiceIcon from '../../../images/ChooseService.svg';

const Indexes = {
  ChooseDataTypeIndex: 0,
  UploadTabIndex: 1,
  LoadingTabIndex: 2
};
function UploadModal() {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const [uploadDisabled, setUploadDisabled] = useState(true);
  const [state, dispatch] = useFileUploadContext();
  const { showModal, acceptedFiles, acceptedCsvHeaders } = state;
  const [ActiveIndex, setActiveIndex] = useState(Indexes.ChooseDataTypeIndex);
  const [SelectedDataTye, setSelectedDataTye] = useState(
    ManualFileUploadTypeList[0].type
  );
  const [redirectSeconds, setRedirectSeconds] = useState(
    UPLOAD_MODAL_REDIRECT_TIME
  );

  const setAcceptedCsvHeaders = (headers: string[]) =>
    dispatch(FileUploadAction.setAcceptedCsvHeaders(headers));
  const apiContext = useContext(ApiContext);
  const { OnUploadClick, getCsvHeaders } = apiContext;
  const closeModal = () => {
    setActiveIndex(Indexes.ChooseDataTypeIndex);
    dispatch(FileUploadAction.hideUploadModal());
  };
  const renderActivetab = (tabIndex: number) => {
    switch (tabIndex) {
      case Indexes.ChooseDataTypeIndex:
        return renderChooseDataTypeTabContent();
      case Indexes.UploadTabIndex:
        return renderUploadtab();
      case Indexes.LoadingTabIndex:
        return renderLoadingStatus();
      default:
        return renderChooseDataTypeTabContent();
    }
  };
  const handleDataTypeChange = (selectedDataType: string) => {
    setSelectedDataTye(selectedDataType);
  };
  const renderChooseDataTypeTabContent = () => {
    return (
      <>
        {renderModalNavigation()}
        <ChooseDataTypeTab
          selectedDataType={SelectedDataTye}
          onSelectDataType={handleDataTypeChange}
        />
        {renderFooterButtons()}
      </>
    );
  };
  const renderUploadtab = () => {
    return (
      <>
        {renderModalNavigation()}
        <UploadTab selectedDataType={SelectedDataTye} />
        {renderFooterButtons()}
      </>
    );
  };
  const startTimer = () => {
    const reduceCountTime = POLLING_MILLI_SECONDS;
    let timer = setTimeout(() => {
      if (redirectSeconds === 0) {
        setRedirectSeconds(UPLOAD_MODAL_REDIRECT_TIME);
        clearTimeout(timer);
        setActiveIndex(Indexes.ChooseDataTypeIndex);
        closeModal();
      } else {
        setRedirectSeconds(redirectSeconds - 1);
      }
    }, reduceCountTime);
  };

  useEffect(() => {
    isUploadDisabled(
      acceptedFiles,
      minSize,
      maxSize,
      acceptedCsvHeaders
    ).then((isDisabled) => setUploadDisabled(isDisabled));
  }, [acceptedFiles]);

  const uploadType = uploadTypeCheck(SelectedDataTye);
  const minSize = minSizeCheck(SelectedDataTye);
  const maxSize = maxSizeCheck(SelectedDataTye);

  const renderLoadingStatus = () => {
    startTimer();
    return (
      <div className="loading-status">
        <div>
          <div className="align-center">
            <Button
              className="upload-modal-loading"
              loading={true}
              disabled={true}
              content="Loading"
            />
          </div>
          <div className="align-center f-20">
            {t(d.UPLOADING)} {acceptedFiles.length} {t(d.FILES)}{' '}
          </div>
          <div className="align-center pt-10">{t(d.UPLOADINGMESSAGE1)}</div>
          <hr className="hr-width mtb-20" />
          <div className="align-center">
            {t(d.REDIRECTMESSAGE1)} {redirectSeconds} {t(d.REDIRECTMESSAGE2)}
          </div>
        </div>
      </div>
    );
  };
  const renderModalNavigation = () => {
    return (
      <div className="modal-tab-button">
        <div
          className={getActivetabClassName(
            ActiveIndex,
            Indexes.ChooseDataTypeIndex
          )}
        >
          <button className="upload-step-button">
            {ActiveIndex === Indexes.ChooseDataTypeIndex && (
              <div className="custom-badge-upload blue">1</div>
            )}
            {ActiveIndex === Indexes.UploadTabIndex && (
              <div className="service-icon">
                <img src={ChooseServiceIcon} />
              </div>
            )}
            <span className="upload-tab-header">{t(d.CHOOSEDATATYPE)}</span>
          </button>
        </div>
        <div className="upload-buttons-separator"></div>
        <div
          className={getActivetabClassName(ActiveIndex, Indexes.UploadTabIndex)}
        >
          <button className="upload-step-button">
            <div
              style={{
                background:
                  ActiveIndex === Indexes.UploadTabIndex ? '#45A7EA' : 'grey'
              }}
              className="custom-badge-upload"
            >
              2
            </div>
            <span className="upload-tab-header">{t(d.UPLOAD)}</span>
          </button>
        </div>
      </div>
    );
  };
  const handleNextButtonClick = async () => {
    if (getCsvHeaders) {
      const headers = await getCsvHeaders(uploadType);
      setAcceptedCsvHeaders(headers);
    }
    setActiveIndex(Indexes.UploadTabIndex);
  };
  const renderFooterButtons = () => {
    return (
      <div className="upload-modal-footer">
        <div>
          <Button
            className="upload-data-cancel upload-footer-button"
            type="secondary"
            onClick={() => {
              closeModal();
              setActiveIndex(Indexes.ChooseDataTypeIndex);
            }}
          >
            {t(d.CANCEL)}
          </Button>
        </div>
        {ActiveIndex == Indexes.ChooseDataTypeIndex && (
          <div>
            <Button
              style={{ marginLeft: 'auto' }}
              type="primary"
              className="upload-footer-button"
              onClick={() => handleNextButtonClick()}
            >
              {t(d.NEXTUPLOAD)}
            </Button>
          </div>
        )}
        {ActiveIndex == Indexes.UploadTabIndex && (
          <div>
            <Button
              disabled={uploadDisabled}
              style={{ marginLeft: 'auto' }}
              type="primary"
              onClick={() => {
                setActiveIndex(Indexes.LoadingTabIndex);
                handleManualUploadClick(
                  acceptedFiles,
                  SelectedDataTye,
                  uploadType,
                  OnUploadClick
                );
              }}
            >
              {t(d.UPLOAD)}
            </Button>
          </div>
        )}
      </div>
    );
  };
  return (
    <React.Fragment>
      <Modal
        className="emaas-upload-modal"
        size="large"
        closeIcon={ActiveIndex === Indexes.LoadingTabIndex ? false : true}
        onClose={closeModal}
        open={showModal}
        closeOnDimmerClick={false}
      >
        <div className="upload-modal-header">{t(d.UPLOADDATA)}</div>
        <Modal.Content className="upload-modal-content">
          {renderActivetab(ActiveIndex)}
        </Modal.Content>
      </Modal>
    </React.Fragment>
  );
}

export default UploadModal;
